import { useEffect, useRef } from "react"
import type { GenerationJobRow } from "@/types/workspace"

export const TAB_JOB_KINDS = {
  storyboard: ["storyboard", "storyboard_correction"],
  video: [
    "video",
    "video_with_reference_prep",
    "video_with_animatic_reference_prep",
    "byteplus_seedance_video_correction",
    "kling_video_correction",
    "topaz_post_production",
    "shot_timeline_render",
    "seedance_draft_finalize",
  ],
  shooting_board: ["shooting_board", "animatic_segment_shooting_board_strip"],
} as const

/** Job kinds that create ShotMediaVersion rows — excludes post-production (separate panel). */
export const MEDIA_VERSION_JOB_KINDS = {
  storyboard: ["storyboard", "storyboard_correction"],
  video: [
    "video",
    "video_with_reference_prep",
    "video_with_animatic_reference_prep",
    "byteplus_seedance_video_correction",
    "kling_video_correction",
    "seedance_draft_finalize",
  ],
  shooting_board: ["shooting_board", "animatic_segment_shooting_board_strip"],
} as const

export type ShotTabJobKey = keyof typeof TAB_JOB_KINDS

export function isInFlightJob(job: GenerationJobRow): boolean {
  return job.status === "queued" || job.status === "processing"
}

export function inFlightSketchEditForShot(
  jobs: GenerationJobRow[],
  shotId: number,
  tab: "storyboard" | "shooting_board",
): GenerationJobRow | null {
  return (
    jobs.find(
      (j) =>
        isInFlightJob(j) &&
        j.job_kind === "sketch_edit" &&
        j.sketch_shot_tab === tab &&
        (j.shot_ids ?? []).includes(shotId),
    ) ?? null
  )
}

export function isStoryboardTileCorrectingJob(job: GenerationJobRow): boolean {
  if (!isInFlightJob(job)) return false
  if (job.job_kind === "storyboard_correction") return true
  return job.job_kind === "sketch_edit" && job.sketch_shot_tab === "storyboard"
}

export function isStoryboardTileGeneratingJob(job: GenerationJobRow): boolean {
  if (!isInFlightJob(job)) return false
  if (job.job_kind === "storyboard" || job.job_kind === "storyboard_correction") return true
  return job.job_kind === "sketch_edit" && job.sketch_shot_tab === "storyboard"
}

function isActiveShotJob(j: GenerationJobRow, shotId: number, kinds: readonly string[]): boolean {
  return isInFlightJob(j) && kinds.includes(j.job_kind) && (j.shot_ids ?? []).includes(shotId)
}

export function activeShotJob(
  jobs: GenerationJobRow[],
  shotId: number,
  kinds: readonly string[],
): GenerationJobRow | null {
  return jobs.find((j) => isActiveShotJob(j, shotId, kinds)) ?? null
}

/** Все идущие генерации вида шота (параллельные запуски) — от ранней к поздней. */
export function activeShotJobs(jobs: GenerationJobRow[], shotId: number, kinds: readonly string[]): GenerationJobRow[] {
  return jobs
    .filter((j) => isActiveShotJob(j, shotId, kinds))
    .sort((a, b) => (a.created_at ?? "").localeCompare(b.created_at ?? "") || a.id - b.id)
}

/** Вкладки шота с идущей генерацией (кадр с корректировкой эскиза, видео, раскадровка) — для спиннера на вкладке. */
export function busyShotTabKeys(jobs: GenerationJobRow[], shotId: number): ShotTabJobKey[] {
  const keys: ShotTabJobKey[] = []
  if (activeShotJob(jobs, shotId, TAB_JOB_KINDS.storyboard) || inFlightSketchEditForShot(jobs, shotId, "storyboard")) {
    keys.push("storyboard")
  }
  if (activeShotJob(jobs, shotId, TAB_JOB_KINDS.video)) keys.push("video")
  if (
    activeShotJob(jobs, shotId, TAB_JOB_KINDS.shooting_board) ||
    inFlightSketchEditForShot(jobs, shotId, "shooting_board")
  ) {
    keys.push("shooting_board")
  }
  return keys
}

/** Unread preview-tab dot only after successful generation (not failed/cancelled). */
export function jobStatusMarksTabUnread(status: string | undefined): boolean {
  return status === "completed"
}

/** Вызывает onFinish, когда активная задача для вкладки завершилась (id пропал из списка). */
export function useReloadOnJobFinish(activeJob: GenerationJobRow | null, onFinish: (finishedJobId: number) => void) {
  const prevIdRef = useRef<number | null>(null)

  useEffect(() => {
    const prev = prevIdRef.current
    const curr = activeJob?.id ?? null
    if (prev != null && prev !== curr && prev > 0) {
      onFinish(prev)
    }
    prevIdRef.current = curr
  }, [activeJob?.id, onFinish])
}
